<script lang="ts" setup>
import { useCityStore } from '@/store/city/city'

import { Breadcrumb } from '@/api/modules/sales/types'
import { HttpStatusCode, NotFoundError, ServerError } from '@/api/results'

/* Components */
import CardPromocode from '@/components/Sales/Sale/CardPromocode.vue'
import SaleImage from '@/components/Sales/Sale/SaleImage.vue'

const route = useRoute()

const {
  cityInfo: { padeg, domain },
} = useCityStore()

const { data: sale, error } = await useAsyncData(async (nuxtApp) => {
  const saleSlug =
    typeof route?.params?.slug === 'string' ? route?.params?.slug : ''

  const result = await nuxtApp?.$apiService.sales.fetchSale(saleSlug)

  if (result instanceof NotFoundError) {
    throw createError({
      statusCode: HttpStatusCode.NOT_FOUND,
    })
  }

  if (result instanceof ServerError || result instanceof Error) {
    throw createError({
      statusCode: HttpStatusCode.INTERNAL_SERVER_ERROR,
    })
  }

  return result?.data
})

if (error.value) showError(error.value)

const breadcrumbs = computed(() => {
  const breadcrumbs: Breadcrumb[] = [
    { text: `Доставка суши в ${padeg?.pred}`, href: '/' },
    { text: 'Акции', href: '/sales/' },
    { text: sale?.value?.title || '', href: sale?.value?.slug || '' },
  ]

  if (sale?.value?.archive) {
    const saleBreadcrumb = breadcrumbs.pop()

    breadcrumbs.push({
      text: 'Архив акций',
      href: '/sales/archive/',
    })

    if (saleBreadcrumb) breadcrumbs.push(saleBreadcrumb)
  }

  return breadcrumbs
})

const actionButton = ref(sale.value?.action_button)

const isActionButtonShow = computed(
  () => actionButton.value && actionButton?.value?.href,
)

const isActionButtonHref = isActionButtonShow.value
  ? checkUrlIsExternal(actionButton.value?.href)
  : undefined

const isActiveCode = computed(() => sale.value?.code?.active || false)
</script>

<template>
  <div class="sale-content">
    <app-head
      :title="sale?.seo?.seo_title"
      :seo="{
        title: sale?.seo?.seo_title,
        description: sale?.seo?.seo_description,
        keywords: sale?.seo?.seo_keywords,
      }"
    />

    <the-breadcrumbs
      v-if="breadcrumbs.length"
      class="sale-content__breadcrumbs"
      :domain="domain"
      :breadcrumbs="breadcrumbs"
    />

    <sale-image
      v-if="sale"
      :image="sale.image"
      :title="sale.title"
      :archive="sale.archive"
    />

    <div v-if="sale?.archive" class="sale-content__archive">
      Акция завершена
    </div>

    <div class="sale-content__section">
      <div class="sale-content__text">
        <h1 class="sale-content__title">
          {{ sale?.title }}
        </h1>

        <div class="sale-content__description" v-html="sale?.description" />
      </div>

      <card-promocode
        v-if="sale?.code && !sale?.archive"
        class="sale-content__promocode"
        :short-description="sale.short_description"
        :promocode="sale.code"
        :is-active-code="isActiveCode"
      />
    </div>

    <nuxt-link :to="actionButton?.href" :external="isActionButtonHref">
      <the-button v-if="isActionButtonShow" class="sale-content__button">
        {{ actionButton?.text }}
      </the-button>
    </nuxt-link>
  </div>
</template>

<style lang="scss" scoped>
.sale-content {
  padding: 30px calc(50% - 480px) 120px !important;

  @include media('<=desktop-mini') {
    padding: 0 48px !important;
  }

  @include media('<=portrait-phone') {
    padding: 0 20px !important;
  }

  &__breadcrumbs {
    margin-top: 20px;

    @include media('<=desktop-mini') {
      margin-top: 20px;
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    margin: 24px 0;
    border-radius: 20px;

    @include media('>=portrait-phone') {
      margin: 24px 0 40px;
      border-radius: 20px;
    }

    @include media('>=desktop-mini') {
      margin: 48px 0 40px;
      border-radius: 40px;
    }
  }

  &__archive {
    width: max-content;
    padding: 3px 10px;
    margin-bottom: 30px;
    font-size: 12px;
    font-weight: 700;
    line-height: 14px;
    color: var(--ui-white);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: var(--ui-text-primary);
    border-radius: 20px;
  }

  &__section {
    margin-bottom: 24px;

    @include media('>=desktop-mini') {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      margin-bottom: 30px;
    }
  }

  &__title {
    margin-bottom: 30px;
    font-size: 16px;
    font-weight: 800;
    line-height: 26px;

    @include media('>=portrait-phone') {
      font-size: 22px;
    }
  }

  &__description {
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;

    :deep(p) {
      margin-bottom: 16px;
    }
  }

  &__promocode {
    width: 100%;
    margin: 24px 0 0 0;

    @include media('>=portrait-phone') {
      width: auto;
    }

    @include media('>=desktop-mini') {
      margin: 0 40px 0 48px;
    }
  }

  &__button {
    width: 260px;

    @include media('<=portrait-phone') {
      width: 100%;
      margin-top: 25px;
    }
  }
}

.header {
  display: -webkit-box;
  display: flex;
  flex-wrap: wrap;
  grid-area: header;
}
</style>
